import { Button, DatePicker, Icon, MuiMenuItem, TextField, Typography, strIsNullOrEmpty, useDebounce } from '@fox/atoms';
import { FlexRowSmallSpaced } from '@fox/layout';
import { Pagination } from '@mui/material';
import MUIAlert from '@mui/material/Alert';
import { Moment } from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { capitalizeFirstLetter } from '../../common/helper/helper';
import { useFetch } from '../../common/use-fetch';
import { useContentContext } from '../content-context';
import { CompactView } from './compact-view';
import { GetNewsByTagsInputModel, GetNewsByTagsResultModel, NewsListItemModel, NewsListProps, NewsListSortType } from './contracts';
import { ExpandedView } from './expanded-view';
import { LeftImageView } from './left-image-view';

// Common styling constants
const CONTROL_HEIGHT = '40px';
const CONTROL_MIN_WIDTH = '10rem';
const CONTROL_MAX_WIDTH = '10rem';
const SEARCH_MAX_WIDTH = '15rem';

// Page size constants
const COMPACT_PAGE_SIZE_OPTIONS = [9, 18, 27];
const DEFAULT_PAGE_SIZE_OPTIONS = [8, 16, 24];

// Styled components
const TagsAndFilterWrapper = styled.div<{ alignment: 'Left' | 'Right' | 'Center (default)' }>`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	align-items: ${props => (props.alignment === 'Left' ? 'flex-start' : props.alignment === 'Right' ? 'flex-end' : 'center')};
`;

const ControlsWrapper = styled.div<{
	$alignment?: 'Left' | 'Right' | 'Center (default)';
	$searchBarOption?: 'full' | 'search-only' | 'none';
	$enableRatings?: boolean;
}>`
	max-width: ${props => (props.$enableRatings ? '1100px' : '970px')};
	width: 100%;
	display: ${props => (props.$searchBarOption === 'none' ? 'none' : 'flex')};
	flex-direction: row;
	gap: 2rem;
	justify-content: ${props =>
		props.$searchBarOption === 'search-only'
			? props.$alignment === 'Left'
				? 'flex-start'
				: props.$alignment === 'Right'
				? 'flex-end'
				: 'center'
			: 'space-between'};
	flex-wrap: wrap;
	padding-bottom: 1rem;
	padding-right: 2rem;
	padding-left: 2rem;

	.filterControl {
		.MuiAutocomplete-root {
			height: ${CONTROL_HEIGHT};
			min-height: ${CONTROL_HEIGHT} !important;
			.MuiFormControl-root {
				height: ${CONTROL_HEIGHT};
				.MuiInputBase-root {
					height: ${CONTROL_HEIGHT};
					.MuiInputBase-input {
						height: 20px;
					}
				}
			}
		}
	}

	${props => (props.$searchBarOption === 'search-only' ? '.filterGroup {display: none;}' : '')}

	@media (max-width: ${props => (props.$enableRatings ? '1100px' : '970px')}) {
		justify-content: center;
	}

	@media (max-width: 767px) {
		padding: 0rem 1rem;
		flex-direction: column;
		gap: 1rem;
		.filterGroup {
			flex-direction: column;
			width: 100% !important;
			gap: 1rem;
		}
		.filterControl {
			min-width: 100% !important;
			max-width: 100% !important;
		}
	}
`;

const TagWrapper = styled.div<{ backgroundColor: string }>`
	background-color: ${props => props.backgroundColor};
	padding: 2rem 2rem 1rem 2rem;
	display: flex;
	row-gap: 0.5rem;
	column-gap: 1.5rem;
	justify-content: center;
	flex-wrap: wrap;

	@media (max-width: 600px) {
		gap: 0.25rem;
	}
`;

const NewsTag = styled(Typography)<{ $isSelected: boolean; $isSelectedBackgroundColor: string }>`
	cursor: pointer;
	padding: 0.5rem 1rem;
	font-size: 1.1rem;
	transition: background-color ease-in-out 0.3s;
	width: max-content;
	background-color: ${props =>
		props.$isSelectedBackgroundColor && props.$isSelected ? props.$isSelectedBackgroundColor : props.$isSelected ? 'black' : 'none'};
	border-radius: 5px;
	border: ${props => (strIsNullOrEmpty(props.$isSelectedBackgroundColor) ? '1px solid black' : 'none')};
	${props => (props.$isSelected && strIsNullOrEmpty(props.$isSelectedBackgroundColor) ? 'color: white;' : '')}

	@media (max-width: 600px) {
		font-size: 1rem;
		padding: 0.25rem 0.5rem;
	}
`;

const PaginationWrapper = styled.div`
	width: 100%;
	justify-content: center;
	display: flex;
	padding: 1rem 0;
`;

const PageSizeOptionsWrapper = styled.div`
	padding: 1rem 0;
	display: flex;
	justify-content: center;
`;

// Export styled components for external use
export { PageSizeOptionsWrapper, PaginationWrapper };

// Types and constants
type SearchBarOptionType = 'full' | 'search-only' | 'none';
type ViewProps = {
	openLinkInNewTab: boolean;
	newsItems: NewsListItemModel[];
	hideThumbnail?: boolean;
	showFullReadMore?: boolean;
	numberOfReadMoreLines?: number;
	readMoreTextColour?: string;
};

const SORT_TYPE_LABELS: Record<NewsListSortType, string> = {
	[NewsListSortType.DateNewestFirst]: 'Newest First',
	[NewsListSortType.DateOldestFirst]: 'Oldest First',
	[NewsListSortType.RatingHighestFirst]: 'Highest Rating First',
	[NewsListSortType.RatingLowestFirst]: 'Lowest Rating First'
};

const SEARCH_BAR_OPTIONS: Record<string, SearchBarOptionType> = {
	'Show full search and filter bar (default)': 'full',
	'Show only search box': 'search-only',
	'Do not show search or filters': 'none'
};

const RATING_OPTIONS = Array.from({ length: 6 }, (_, i) => i); // [0, 1, 2, 3, 4, 5]

// Utility functions
const getSearchBarOption = (option: string | undefined): SearchBarOptionType => {
	return SEARCH_BAR_OPTIONS[option || ''] || 'full';
};

const getSortTypeDisplayText = (sortType: NewsListSortType): string => {
	return SORT_TYPE_LABELS[sortType] || SORT_TYPE_LABELS[NewsListSortType.DateNewestFirst];
};

const getAvailableSortTypes = (enableRatings: boolean): NewsListSortType[] => {
	const dateSortTypes = [NewsListSortType.DateNewestFirst, NewsListSortType.DateOldestFirst];
	return enableRatings ? [...dateSortTypes, NewsListSortType.RatingHighestFirst, NewsListSortType.RatingLowestFirst] : dateSortTypes;
};

const getMinimumRatingDisplayText = (rating: number): string => {
	return rating === 0 ? 'Show All' : `${rating}+`;
};

const getPageSizeOptions = (displayMode: string): number[] => {
	return displayMode === 'Compact' ? COMPACT_PAGE_SIZE_OPTIONS : DEFAULT_PAGE_SIZE_OPTIONS;
};

// Component functions
const NewsRender: React.FC<ViewProps & { display: string }> = ({ display, ...viewProps }) => {
	const viewComponents = {
		Compact: CompactView,
		LeftImage: LeftImageView
	};

	const ViewComponent = viewComponents[display as keyof typeof viewComponents] || ExpandedView;
	return <ViewComponent {...viewProps} />;
};

// Sub-components
const TagSelector: React.FC<{
	tags: string[];
	selectedTags: string[];
	onTagToggle: (tag: string) => void;
	onClearAll: () => void;
	highlightColour?: string;
	backgroundColor?: string;
}> = ({ tags, selectedTags, onTagToggle, onClearAll, highlightColour, backgroundColor }) => {
	if (!tags?.length) return null;

	return (
		<TagWrapper backgroundColor={backgroundColor || 'none'}>
			<NewsTag
				variant="h5"
				$isSelected={selectedTags.length === 0}
				$isSelectedBackgroundColor={highlightColour || ''}
				key="see-all"
				onClick={onClearAll}
			>
				See all
			</NewsTag>
			{tags.map(tag => {
				const isSelected = selectedTags.includes(tag);
				return (
					<NewsTag
						variant="h5"
						$isSelected={isSelected}
						$isSelectedBackgroundColor={highlightColour || ''}
						key={tag}
						onClick={() => onTagToggle(tag)}
					>
						{capitalizeFirstLetter(tag)}
					</NewsTag>
				);
			})}
		</TagWrapper>
	);
};

const SearchField: React.FC<{
	value: string;
	onChange: (value: string) => void;
}> = ({ value, onChange }) => (
	<TextField
		className="filterControl"
		value={value}
		onChange={e => onChange(e.target.value)}
		autoFocus
		sx={{ minWidth: CONTROL_MIN_WIDTH, maxWidth: SEARCH_MAX_WIDTH }}
		size="small"
		placeholder="Search"
		endAdornment={<Icon iconName="Search" />}
	/>
);

const DateFilters: React.FC<{
	from: Date | null;
	to: Date | null;
	onFromChange: (date: Date | null) => void;
	onToChange: (date: Date | null) => void;
}> = ({ from, to, onFromChange, onToChange }) => (
	<FlexRowSmallSpaced className="filterGroup">
		<DatePicker
			className="filterControl"
			label="Date from"
			onChange={(value: Moment) => onFromChange(value.toDate())}
			size="small"
			sx={{ minWidth: CONTROL_MIN_WIDTH, maxWidth: CONTROL_MAX_WIDTH, textAlign: 'left' }}
		/>
		<DatePicker
			className="filterControl"
			label="Date to"
			onChange={(value: Moment) => onToChange(value.toDate())}
			size="small"
			sx={{ minWidth: CONTROL_MIN_WIDTH, maxWidth: CONTROL_MAX_WIDTH, textAlign: 'left' }}
		/>
	</FlexRowSmallSpaced>
);

const SortAndRatingFilters: React.FC<{
	sortType: NewsListSortType;
	onSortChange: (sortType: NewsListSortType) => void;
	enableRatings: boolean;
	minRating: number;
	onRatingChange: (rating: number) => void;
	onReset: () => void;
	isResetDisabled: boolean;
}> = ({ sortType, onSortChange, enableRatings, minRating, onRatingChange, onReset, isResetDisabled }) => (
	<FlexRowSmallSpaced className="filterGroup">
		<TextField
			className="filterControl"
			size="small"
			label="Sort by"
			sx={{ minWidth: CONTROL_MIN_WIDTH, maxWidth: CONTROL_MAX_WIDTH, textAlign: 'left' }}
			value={sortType}
			select
			onChange={e => {
				const value = parseInt(e.target.value);
				const availableSortTypes = getAvailableSortTypes(enableRatings);
				if (availableSortTypes.includes(value as NewsListSortType)) {
					onSortChange(value as NewsListSortType);
				}
			}}
		>
			{getAvailableSortTypes(enableRatings).map(sortOption => (
				<MuiMenuItem key={sortOption} value={sortOption}>
					{getSortTypeDisplayText(sortOption)}
				</MuiMenuItem>
			))}
		</TextField>

		{enableRatings && (
			<TextField
				className="filterControl"
				size="small"
				label="Minimum Rating"
				sx={{ minWidth: CONTROL_MIN_WIDTH, maxWidth: CONTROL_MAX_WIDTH, textAlign: 'left' }}
				value={minRating}
				select
				onChange={e => {
					const value = parseInt(e.target.value);
					if (value >= 0 && value <= 5) {
						onRatingChange(value);
					}
				}}
			>
				{RATING_OPTIONS.map(rating => (
					<MuiMenuItem key={rating} value={rating}>
						{getMinimumRatingDisplayText(rating)}
					</MuiMenuItem>
				))}
			</TextField>
		)}

		<Button sx={{ padding: '0.25rem 0.5rem', fontSize: '1.1rem' }} disabled={isResetDisabled} onClick={onReset} variant="secondary">
			Reset
		</Button>
	</FlexRowSmallSpaced>
);

const PaginationControls: React.FC<{
	totalPages: number;
	currentPage: number;
	onPageChange: (page: number) => void;
	pageSize: number;
	onPageSizeChange: (size: number) => void;
	pageSizeOptions: number[];
}> = ({ totalPages, currentPage, onPageChange, pageSize, onPageSizeChange, pageSizeOptions }) => (
	<>
		{totalPages > 1 && (
			<PaginationWrapper>
				<Pagination
					style={{ alignSelf: 'center' }}
					count={totalPages}
					page={currentPage}
					onChange={(_, value) => onPageChange(value)}
					color="secondary"
				/>
			</PaginationWrapper>
		)}
		<PageSizeOptionsWrapper>
			<TextField
				size="small"
				sx={{ width: '150px' }}
				label="Items per page"
				value={pageSize}
				select
				onChange={e => onPageSizeChange(Number(e.target.value))}
			>
				{pageSizeOptions.map(option => (
					<MuiMenuItem value={option} key={option}>
						{option}
					</MuiMenuItem>
				))}
			</TextField>
		</PageSizeOptionsWrapper>
	</>
);

// Main component
export const NewsList: React.FC<NewsListProps> = ({
	openLinkInNewTab,
	tags,
	rSSFeedUrl,
	selectedTagHighlightColour,
	tagSelectorBackgroundColour,
	displayMode,
	hideThumbnail,
	filterAlignment,
	numberOfReadMoreLines,
	showFullReadMore,
	searchBarOption,
	readMoreTextColour,
	disablePagination,
	authors,
	enableRatings = false
}) => {
	// State management
	const pageSizeOptions = useMemo(() => getPageSizeOptions(displayMode || ''), [displayMode]);
	const { contentId } = useContentContext();

	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	const [page, setSelectedPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(pageSizeOptions[0]);
	const [search, setSearch] = useState<string>('');
	const [from, setFrom] = useState<Date | null>(null);
	const [to, setTo] = useState<Date | null>(null);
	const [sortType, setSortType] = useState<NewsListSortType>(NewsListSortType.DateNewestFirst);
	const [minRating, setMinRating] = useState<number>(0);

	const debouncedSearch = useDebounce(search, 500);
	const searchBarOptionValue = useMemo(() => getSearchBarOption(searchBarOption), [searchBarOption]);

	// Event handlers
	const handleTagToggle = useCallback((tag: string) => {
		setSelectedTags(prev => {
			const isSelected = prev.includes(tag);
			const newTags = isSelected ? prev.filter(t => t !== tag) : [...prev, tag];
			setSelectedPage(1);
			return newTags;
		});
	}, []);

	const handleTagClearAll = useCallback(() => {
		setSelectedTags([]);
		setSelectedPage(1);
	}, []);

	const handleRatingChange = useCallback((rating: number) => {
		setMinRating(rating);
		setSelectedPage(1);
	}, []);

	const handlePageSizeChange = useCallback((size: number) => {
		setPageSize(size);
		setSelectedPage(1);
	}, []);

	const handleReset = useCallback(() => {
		setSearch('');
		setFrom(null);
		setTo(null);
		setSortType(NewsListSortType.DateNewestFirst);
		setSelectedTags([]);
		if (enableRatings) {
			setMinRating(0);
		}
	}, [enableRatings]);

	// Computed values
	const isResetDisabled = useMemo(() => {
		return (
			strIsNullOrEmpty(search) &&
			from === null &&
			to === null &&
			!selectedTags.length &&
			sortType === NewsListSortType.DateNewestFirst &&
			(!enableRatings || minRating === 0)
		);
	}, [search, from, to, selectedTags.length, sortType, enableRatings, minRating]);

	// API call
	const { loading, error, result } = useFetch<GetNewsByTagsResultModel, GetNewsByTagsInputModel>(
		{
			page,
			pageSize: Number(pageSize),
			contentId,
			tags: selectedTags,
			rSSFeedUrl,
			from,
			search: debouncedSearch,
			sortBy: sortType,
			to,
			showFullReadMore,
			disablePagination,
			minRating: enableRatings && minRating > 0 ? minRating : undefined,
			authors
		},
		'/Umbraco/Api/NewsSearch/GetNewsByTags',
		'POST',
		['tags', 'page', 'pageSize', 'search', 'from', 'to', 'sortBy', ...(enableRatings ? ['minRating'] : []), 'showFullReadMore']
	);

	if (error) {
		return <MUIAlert severity="error">{error}</MUIAlert>;
	}

	return (
		<div>
			<TagsAndFilterWrapper alignment={filterAlignment || 'Center (default)'}>
				<TagSelector
					tags={tags}
					selectedTags={selectedTags}
					onTagToggle={handleTagToggle}
					onClearAll={handleTagClearAll}
					highlightColour={selectedTagHighlightColour}
					backgroundColor={tagSelectorBackgroundColour}
				/>

				<ControlsWrapper $alignment={filterAlignment} $searchBarOption={searchBarOptionValue} $enableRatings={enableRatings}>
					<SearchField value={search} onChange={setSearch} />

					<DateFilters from={from} to={to} onFromChange={setFrom} onToChange={setTo} />

					<SortAndRatingFilters
						sortType={sortType}
						onSortChange={setSortType}
						enableRatings={enableRatings}
						minRating={minRating}
						onRatingChange={handleRatingChange}
						onReset={handleReset}
						isResetDisabled={isResetDisabled}
					/>
				</ControlsWrapper>
			</TagsAndFilterWrapper>

			{result?.newsItems?.length ? (
				<NewsRender
					display={displayMode || ''}
					openLinkInNewTab={openLinkInNewTab}
					newsItems={result.newsItems}
					hideThumbnail={hideThumbnail}
					numberOfReadMoreLines={numberOfReadMoreLines}
					showFullReadMore={showFullReadMore}
					readMoreTextColour={readMoreTextColour}
				/>
			) : (
				<MUIAlert sx={{ maxWidth: '40rem', margin: '1rem auto' }} severity="info">
					No news items to display
				</MUIAlert>
			)}

			{!disablePagination && result && (
				<PaginationControls
					totalPages={result.totalPages}
					currentPage={page}
					onPageChange={setSelectedPage}
					pageSize={pageSize}
					onPageSizeChange={handlePageSizeChange}
					pageSizeOptions={pageSizeOptions}
				/>
			)}
		</div>
	);
};
